<!-- eslint-disable vue/no-v-html -->
<template>
  <div :class="className">
    <div v-if="!isUser" class="node-message__avatar_container">
      <img v-if="avatar" class="node-message__avatar" :src="avatar" alt="avatar" />
      <span v-if="!avatar" class="node-message__avatar"> R </span>
    </div>

    <div v-if="$slots.default" class="node-message__content">
      <slot />
    </div>

    <QuickPromptMessage v-else-if="isQuickPrompt" :message="message" />

    <div v-else class="node-message__content">
      <template v-if="message.chunks?.length">
        <template v-for="(chunk, index) in groupedChunks" :key="index">
          <TextMessageWithTooltip v-if="chunk.type === CHUNK_TYPE.TEXT" :message="message" :content="chunk.content" />

          <ToolCallBlockSSE
            v-else-if="chunk.type === CHUNK_TYPE.TOOL_CALLS"
            :calls="chunk.calls"
            :followed-by-text="chunk.followedByText"
            :awaiting-approval="chunk.awaitingApproval"
            :interrupt="latestInterrupt"
            :sanitizeAndParse="sanitizeAndParse"
            :is-typing="chunk.isTyping"
          />

          <ReferenceCardsList
            v-else-if="chunk.type === CHUNK_TYPE.REFERENCES"
            :data="chunk.data"
            :message="message"
            :messageId="message.messageId"
          />

          <InterruptBlockSSE v-else-if="chunk.type === CHUNK_TYPE.INTERRUPT" :chunk="chunk" />

          <ImageGenBlockSSE
            v-else-if="chunk.type === CHUNK_TYPE.IMAGE_GEN"
            :chunk="chunk"
            :message-id="message.messageId"
          />

          <ExecutionErrorMessage v-else-if="chunk.type === CHUNK_TYPE.ERROR" :content="chunk.content" />

          <TargetSourceTranslationBlock
            v-else-if="chunk.type === CHUNK_TYPE.TRANSLATION"
            :data="chunk.data"
            :messageId="message.messageId"
          />
        </template>
      </template>
      <TextMessageWithTooltip v-else :message="message" />
      <hr v-if="isQuickPrompt" class="divider" />
      <div v-if="hasData">
        <transition v-for="(blockData, index) in message.data" :key="index" name="slow-fade">
          <component
            v-bind="blockData"
            :is="getMessageBlock(blockData)"
            :data="blockData"
            :messageId="message.messageId"
            :message="message"
          />
        </transition>
      </div>
      <div v-if="showFeedback" class="actions-container">
        <div class="download-dropdown">
          <BbBtn
            size="small"
            type="secondary"
            :icon="isDownloadInProgress ? 'spin' : 'download'"
            icon-type="outlined"
            is-icon-button
            without-background
            :is-disabled="isDownloadInProgress"
            @click.stop="showDownloadMenu = !showDownloadMenu"
          />

          <div v-if="showDownloadMenu" class="download-menu">
            <button class="download-menu__item" @click="downloadMessage('html')" :disabled="isDownloadInProgress">
              HTML
            </button>
            <button class="download-menu__item" @click="downloadMessage('docx')" :disabled="isDownloadInProgress">
              DOCX
            </button>
          </div>
        </div>
        <transition v-if="showFeedback" name="slow-fade">
          <Feedback :message-id="message.messageId" />
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue';

import { CHUNK_TYPE, Role } from '~/constants/conversationModels';
import { collectReplySteps, groupMessageChunks, placeStepsBlock, sanitizeAndParse } from '~/utils/message';

import arcaneLogo from '~/assets/svg/Arcane-logo.svg';
import chatbotLogo from '~/assets/svg/new-chatbot-logo.svg';

import QuickPromptMessage from '~/components/conversation/chatMessage/QuickPromptMessage';
import TextMessageWithTooltip from '~/components/conversation/chatMessage/TextMessageWithTooltip';
import ReferenceCardsList from '~/components/conversation/referencePanel/ReferenceCardsList';
import Feedback from '~/components/conversation/chatMessage/Feedback';
import TargetSourceTranslationBlock from '~/components/conversation/chatMessage/TargetSourceTranslationBlock';
import GeneratedImageBlock from '~/components/conversation/chatMessage/GeneratedImageBlock';
import ToolCallBlockSSE from '~/components/conversation/chatMessage/ToolCall/ToolCallBlockSSE';
import InterruptBlockSSE from '~/components/conversation/chatMessage/InterruptBlockSSE';
import ImageGenBlockSSE from '~/components/conversation/chatMessage/ImageGenBlockSSE';
import ExecutionErrorMessage from '~/components/conversation/chatMessage/ExecutionErrorMessage';
import useAuthoring from '~/composables/useAuthoring';
import useConversations from '~/composables/useConversations';

export default {
  name: 'Message',
  components: {
    TargetSourceTranslationBlock,
    Feedback,
    ReferenceCardsList,
    GeneratedImageBlock,
    TextMessageWithTooltip,
    QuickPromptMessage,
    ToolCallBlockSSE,
    InterruptBlockSSE,
    ImageGenBlockSSE,
    ExecutionErrorMessage,
  },
  props: {
    message: {
      type: Object,
      default: () => {},
    },
    // continues the previous assistant message (e.g. the reply streamed after approving a tool call)
    isContinuation: {
      type: Boolean,
      default: false,
    },
    reply: {
      type: Object,
      default: null,
    },
  },

  setup(props) {
    const { isAuthoringPage } = useAuthoring();
    const { downloadMessageById, currentConversation } = useConversations();

    const isDownloadInProgress = ref(false);

    const isUser = computed(() => props.message.role === Role.user);
    const className = computed(
      () => `node-message ${isUser.value ? 'is-user' : ''} ${props.isContinuation ? 'is-continuation' : ''}`,
    );
    const avatar = computed(() => chatbotLogo);
    const hasData = computed(() => props.message.data && props.message.data.length > 0);
    // Hide feedback while the message's latest content is a tool call/approval, but show it again
    // once real text follows (e.g. the final answer after the tool call has been resolved).
    const hasPendingToolCall = computed(() => {
      const chunks = (props.message.chunks || []).filter(
        (chunk) => chunk.type !== CHUNK_TYPE.TEXT || chunk.content?.trim(),
      );
      const lastChunk = chunks[chunks.length - 1];
      return lastChunk?.type === CHUNK_TYPE.TOOL_CALLS || lastChunk?.type === CHUNK_TYPE.INTERRUPT;
    });
    const showFeedback = computed(
      () =>
        !props.message.isTyping &&
        !isUser.value &&
        !props.message.systemMessage &&
        !isAuthoringPage.value &&
        !hasPendingToolCall.value,
    );
    const isQuickPrompt = computed(() => !!props.message.versionContentId);

    const showDownloadMenu = ref(false);

    const groupedChunks = computed(() => {
      const groups = groupMessageChunks(props.message.chunks || []);
      const reply = props.reply ?? {
        ownsSteps: true,
        ...collectReplySteps([groups]),
        isTyping: props.message.isTyping,
      };
      const block =
        reply.ownsSteps && reply.steps.length
          ? {
              type: CHUNK_TYPE.TOOL_CALLS,
              calls: reply.steps,
              followedByText: reply.followedByText,
              awaitingApproval: reply.awaitingApproval,
              isTyping: reply.isTyping,
            }
          : null;
      return placeStepsBlock(groups, block);
    });

    const latestInterrupt = computed(() => {
      const messageChunks = props.message?.chunks || [];
      for (let i = messageChunks.length - 1; i >= 0; i -= 1) {
        const candidate = messageChunks[i];
        if (candidate?.type !== CHUNK_TYPE.INTERRUPT || !Array.isArray(candidate?.data)) {
          continue;
        }

        if (candidate.data.length > 0) {
          return candidate.data[0];
        }
      }

      return null;
    });

    const getMessageBlock = (blockData) => {
      let component = null;
      switch (blockData.type) {
        case 'documentTranslation':
        case 'documentAttachmentTranslation':
          component = TargetSourceTranslationBlock;
          break;
        case 'documentList':
          component = ReferenceCardsList;
          break;
        case 'imageBlock':
          component = GeneratedImageBlock;
          break;
        default:
          console.log('Invalid type of block message', blockData);
      }

      return component;
    };

    const downloadMessage = async (fileType) => {
      if (isDownloadInProgress.value) return;
      isDownloadInProgress.value = true;
      showDownloadMenu.value = false;
      const url = await downloadMessageById({
        messageId: props.message.messageId,
        conversationId: currentConversation.value.conversationId,
        conversationUserId: currentConversation.value?.userId,
        ...(fileType && { fileType }),
      });

      if (url && fileType === 'html') {
        window.open(url, '_blank');
      }
      isDownloadInProgress.value = false;
    };

    return {
      avatar,
      arcaneLogo,
      className,
      getMessageBlock,
      downloadMessage,
      isDownloadInProgress,
      showDownloadMenu,
      isUser,
      hasData,
      showFeedback,
      isQuickPrompt,
      groupedChunks,
      latestInterrupt,
      sanitizeAndParse,
      CHUNK_TYPE,
    };
  },
};
</script>

<style lang="scss" scoped>
.node-message {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding-top: $bb-spacing-6;

  &__avatar_container {
    background: #fdb91a;
    border-radius: $bb-spacing-6;
    width: $bb-spacing-9;
    height: $bb-spacing-9;
    display: flex;
  }

  &__avatar {
    @include bb-medium-text-styles('xs');

    width: $bb-spacing-5;
    height: $bb-spacing-5;
    margin: auto;
    flex-shrink: 0;

    color: $bb-neutral-0;
    text-align: center;
    line-height: $bb-spacing-6;
  }

  &__content {
    padding: 0 $bb-spacing-4;
    line-height: normal;

    background: $bb-neutral-0;
    max-width: 80%;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-2;

    .slow-fade-enter-active,
    .slow-fade-leave-active {
      transition: opacity 3s; /* Adjust duration for slower/faster effect */
    }

    .slow-fade-enter-from,
    .slow-fade-leave-to {
      opacity: 2;
    }

    .divider {
      border: 0;
      height: 1px;
      background-color: #ccc;
      margin-top: $bb-spacing-1;
      margin-bottom: $bb-spacing-1;
    }
  }

  .highlighted {
    box-shadow: 0px 0px 8px rgba(65, 130, 239, 0.97);
  }

  .logo {
    height: 38px;
    cursor: pointer;
  }

  /* User message styling */
  &.is-user {
    flex-direction: row-reverse;
  }

  &.is-user &__content {
    color: $bb-neutral-1;
    border-radius: $bb-spacing-6;
    background-color: rgba(98, 34, 227, 0.1);
    min-height: $bb-spacing-11;
    padding: $bb-spacing-3 $bb-spacing-5;

    & > * {
      @include bb-medium-text-styles('default');
    }
  }

  &.is-continuation {
    padding-top: $bb-spacing-2;
  }

  &.is-continuation &__avatar_container {
    visibility: hidden;
  }

  .actions-container {
    display: flex;

    button {
      margin-right: $bb-spacing-1;
    }
  }

  .download-dropdown {
    position: relative;
    display: inline-block;
  }

  .download-menu {
    position: absolute;
    right: 0;
    bottom: calc(100% + 4px);
    background: $bb-neutral-0;
    border: 1px solid #e6e6e6;
    border-radius: 4px;
    padding: 4px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.08);
    z-index: 10;
  }

  .download-menu__item {
    background: transparent;
    border: none;
    padding: $bb-spacing-2 $bb-spacing-3;
    text-align: left;
    cursor: pointer;
    @include bb-medium-text-styles('default');
  }

  .download-menu__item:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
